import { config, services } from "@/config";
import {
  activeReservas,
  buildCalendar,
  byCategory,
  featuredServices,
  findService,
  takenTimes,
  totalAgendado,
} from "@/services/booking";
import {
  escapeHTML,
  formatDateLong,
  formatDateShort,
  money,
} from "@/services/format";
import { store } from "@/services/storage";
import { waChatLink } from "@/services/whatsapp";
import { state } from "@/state";
import type { Reserva, Service } from "@/types";
import { byId, refreshIcons, scrollToTop } from "./dom";

const esc = escapeHTML;

/* ============================================================
   Helpers de marcado
   ============================================================ */

function priceBlock(s: Service): string {
  return `<div class="service-price"><span class="amt">${money(s.price)}</span><span class="dur">${esc(s.note ?? "")}</span></div>`;
}

function emptyState(icon: string, title: string, body: string, cta?: string): string {
  return `<div class="empty-state">
    <div class="icon-wrap"><i data-lucide="${icon}" style="width:28px;height:28px"></i></div>
    <p style="font-family:var(--serif);font-size:20px;color:var(--text);margin-bottom:6px">${title}</p>
    <p style="font-size:14px;max-width:340px;margin:0 auto${cta ? " 18px" : ""}">${body}</p>
    ${cta ?? ""}
  </div>`;
}

function reservaCard(r: Reserva, showStatus: boolean): string {
  const d = formatDateShort(r.date);
  const cancelBtn = showStatus
    ? ""
    : `<button type="button" class="btn btn-ghost btn-sm" data-cancel="${esc(r.id)}" aria-label="Cancelar la reserva de ${esc(r.service)} del ${d.day} de ${d.month}">Cancelar</button>`;
  const tag = showStatus
    ? `<span class="tag ${r.status === "cancelada" ? "tag-cancel" : "tag-conf"}" style="margin-top:6px">${esc(r.status)}</span>`
    : `<span class="tag tag-conf" style="margin-top:6px">${esc(r.status)}</span>`;
  return `<div class="reserva-card">
    <div class="date-badge"><div class="dd">${d.day}</div><div class="mm">${d.month}</div></div>
    <div class="info">
      <h4>${esc(r.service)}</h4>
      <div class="meta">${esc(r.name)}${showStatus ? ` · ${esc(r.phone)}` : ""} · ${r.time}${showStatus ? "" : " hrs"}${showStatus ? "" : ` · ${money(r.price || 0)}`}</div>
      ${!showStatus && r.note ? `<div class="meta" style="font-style:italic">"${esc(r.note)}"</div>` : ""}
      ${tag}
    </div>
    ${cancelBtn}
  </div>`;
}

/* ============================================================
   Home y servicios
   ============================================================ */

export function renderHomeServices(): void {
  const box = byId("homeServices");
  box.innerHTML = featuredServices(services, config.featuredServices)
    .map(
      (s) => `<div class="service-row">
        <div class="service-info"><h4>${esc(s.name)}</h4><div class="meta">${esc(s.cat)} · ${s.dur} min</div></div>
        ${priceBlock(s)}
        <button type="button" class="btn btn-primary btn-sm" data-book="${esc(s.id)}" aria-label="Reservar ${esc(s.name)}">Reservar</button>
      </div>`,
    )
    .join("");
  refreshIcons();
}

export function renderServices(): void {
  byId("catTabs").innerHTML = config.categories
    .map(
      (cat) =>
        `<button type="button" class="cat-tab${cat === state.activeCat ? " active" : ""}" data-cat="${esc(cat)}" aria-pressed="${cat === state.activeCat}">${esc(cat)}</button>`,
    )
    .join("");

  byId("serviceList").innerHTML = byCategory(services, state.activeCat)
    .map(
      (s) => `<div class="service-row">
        <div class="service-info"><h4>${esc(s.name)}</h4><div class="meta">${s.dur} min · incluye café y masaje de manos</div></div>
        ${priceBlock(s)}
        <button type="button" class="btn btn-gold btn-sm" data-book="${esc(s.id)}" aria-label="Reservar ${esc(s.name)}">Reservar</button>
      </div>`,
    )
    .join("");
}

/* ============================================================
   Asistente de reserva
   ============================================================ */

export function renderBookServices(): void {
  byId("bookServiceList").innerHTML = services
    .map((s) => {
      const selected = state.booking.service === s.id;
      return `<div class="service-row"${selected ? ' style="border-color:var(--accent);background:var(--accent-soft)"' : ""}>
        <div class="service-info"><h4>${esc(s.name)}</h4><div class="meta">${esc(s.cat)} · ${s.dur} min</div></div>
        <div class="service-price"><span class="amt">${money(s.price)}</span></div>
        <button type="button" class="btn ${selected ? "btn-primary" : "btn-ghost"} btn-sm" data-pickservice="${esc(s.id)}" aria-pressed="${selected}">${selected ? "Seleccionado" : "Elegir"}</button>
      </div>`;
    })
    .join("");
  refreshIcons();
}

export function showStep(step: number): void {
  state.step = step;
  document.querySelectorAll<HTMLElement>(".wizard-panel").forEach((panel) => {
    panel.classList.toggle("active", panel.id === `panel-${step}`);
  });
  document.querySelectorAll<HTMLElement>(".step-pill").forEach((pill) => {
    const n = Number(pill.dataset.step ?? 0);
    pill.classList.toggle("active", n === step);
    pill.classList.toggle("done", n < step);
    if (n === step) pill.setAttribute("aria-current", "step");
    else pill.removeAttribute("aria-current");
  });
  if (step === 2) renderCalendar();
  if (step === 3) renderSlots();
  if (step === 4) renderSummary();
  refreshIcons();
  scrollToTop();
}

export function renderCalendar(): void {
  const days = buildCalendar(new Date(), 14, config.closedWeekdays);
  byId("calendar").innerHTML = days
    .map((d) => {
      const selected = state.booking.date === d.iso;
      const label = `${d.dow} ${d.day} de ${d.month}${d.closed ? " · cerrado" : ""}`;
      return `<button type="button" class="day-cell${d.closed ? " off" : ""}${selected ? " selected" : ""}" data-date="${d.iso}" aria-pressed="${selected}" aria-disabled="${d.closed}" aria-label="${label}">
        <span class="dow">${d.dow}</span><span class="dnum">${d.day}</span><span class="mon">${d.month}</span>
      </button>`;
    })
    .join("");
  refreshIcons();
}

export function renderSlots(): void {
  const taken = takenTimes(store.getReservas(), state.booking.date ?? "");
  byId("slots").innerHTML = config.slots
    .map((time) => {
      const isTaken = taken.includes(time);
      const selected = state.booking.time === time;
      return `<button type="button" class="slot${isTaken ? " taken" : ""}${selected ? " selected" : ""}" data-time="${time}" aria-pressed="${selected}" aria-disabled="${isTaken}">${time}</button>`;
    })
    .join("");
}

export function renderSummary(): void {
  const service = findService(services, state.booking.service);
  const dateStr = state.booking.date ? formatDateLong(state.booking.date) : "—";
  const rows: Array<[string, string]> = [
    ["Servicio", service?.name ?? "—"],
    ["Fecha", dateStr],
    ["Hora", state.booking.time ?? "—"],
    ["Duración", service ? `${service.dur} min` : "—"],
    ["Valor", service ? money(service.price) : "—"],
  ];
  byId("summary").innerHTML = rows
    .map(([label, value]) => `<div class="row"><span>${label}</span><span>${esc(value)}</span></div>`)
    .join("");
}

/* ============================================================
   Agenda de reservas + panel administrativo
   ============================================================ */

export function renderReservas(): void {
  const box = byId("reservaList");
  const upcoming = activeReservas(store.getReservas());

  if (upcoming.length === 0) {
    box.innerHTML = emptyState(
      "calendar-x",
      "Aún no tienes horas reservadas",
      "Reserva tu primera sesión en menos de un minuto y la verás aquí guardada.",
      '<button type="button" class="btn btn-primary btn-sm" data-nav="reservar">Reservar hora</button>',
    );
  } else {
    box.innerHTML = upcoming
      .slice()
      .reverse()
      .map((r) => reservaCard(r, false))
      .join("");
  }

  refreshIcons();
  renderAdmin();
}

export function renderAdmin(): void {
  const reservas = store.getReservas();
  const contactos = store.getContactos();

  byId("adminStats").innerHTML = `<div class="box"><strong>${activeReservas(reservas).length}</strong><span>Reservas activas</span></div>
    <div class="box"><strong>${contactos.length}</strong><span>Clientes</span></div>
    <div class="box"><strong>${money(totalAgendado(reservas))}</strong><span>Total agendado</span></div>`;

  byId("adminReservas").innerHTML =
    reservas.length === 0
      ? '<p style="color:var(--muted);font-size:14px">Sin reservas aún.</p>'
      : reservas
          .slice()
          .reverse()
          .map((r) => reservaCard(r, true))
          .join("");

  byId("adminContactos").innerHTML =
    contactos.length === 0
      ? '<p style="color:var(--muted);font-size:14px">Sin clientes registrados.</p>'
      : contactos
          .map(
            (c) => `<div class="contact-card"><div class="head">
              <div class="avatar" aria-hidden="true">${esc(c.name.charAt(0).toUpperCase())}</div>
              <div><div class="nm">${esc(c.name)}</div><div class="ph">${esc(c.phone)}</div></div>
            </div></div>`,
          )
          .join("");

  refreshIcons();
}

/* ============================================================
   Clientes (historial de servicios)
   ============================================================ */

export function renderContactos(): void {
  const query = byId<HTMLInputElement>("contactSearch").value.trim().toLowerCase();
  const contactos = store.getContactos().filter((c) => {
    return (
      !query ||
      c.name.toLowerCase().includes(query) ||
      (c.phone ?? "").includes(query)
    );
  });

  const box = byId("contactList");
  if (contactos.length === 0) {
    box.innerHTML = emptyState(
      "user-plus",
      query ? "Sin coincidencias" : "Sin clientes registrados",
      query
        ? "Prueba con otro nombre o teléfono."
        : "Agrega a tu primer cliente y guarda su historial de servicios.",
    );
  } else {
    box.innerHTML = contactos
      .map((c) => {
        const history = (c.history ?? [])
          .slice(-3)
          .reverse()
          .map((entry) => esc(entry))
          .join("<br>");
        return `<div class="contact-card">
          <div class="head">
            <div class="avatar" aria-hidden="true">${esc(c.name.charAt(0).toUpperCase())}</div>
            <div style="flex:1"><div class="nm">${esc(c.name)}</div><div class="ph">${esc(c.phone)}</div></div>
            <a class="btn btn-ghost btn-sm" href="${waChatLink(c.phone)}" target="_blank" rel="noopener" aria-label="Escribir por WhatsApp a ${esc(c.name)}"><i data-lucide="message-circle" style="width:14px;height:14px"></i></a>
            <button type="button" class="btn btn-ghost btn-sm" data-delcontact="${esc(c.id)}" aria-label="Eliminar a ${esc(c.name)}"><i data-lucide="trash-2" style="width:14px;height:14px"></i></button>
          </div>
          ${c.notes ? `<div class="hist"><strong style="color:var(--text)">Notas:</strong> ${esc(c.notes)}</div>` : ""}
          ${history ? `<div class="hist"><strong style="color:var(--text)">Últimas sesiones:</strong><br>${history}</div>` : ""}
        </div>`;
      })
      .join("");
  }

  refreshIcons();
}

/** Renderiza todo lo que no depende de la navegación (arranque). */
export function renderInitial(): void {
  renderHomeServices();
  renderServices();
  renderBookServices();
}
